<template>
  <div v-show="!loading" class="bg-white flex-col justify-start items-center gap-4 flex">
    <div class="self-stretch flex-col justify-start items-center gap-3 flex">
      <div class="self-stretch flex-col justify-start items-center gap-1 flex">
        <div class="text-center text-racq-med-grey text-xl2-bold racq-max-w-xl">
          <Text :field="{ value: headingTextReplaced }" />
        </div>
      </div>
      <div v-if="icon?.fileName" class="flex items-center justify-center h-[88px] w-[88px]">
        <img
          :src="icon.fileName"
          :alt="icon.alt"
          :class="[
            'mb-2',
            icon.rounded === undefined || icon.rounded ? 'rounded-full' : '',
            icon.width ? `w-[${icon.width}px]` : 'w-[72px]',
            icon.height ? `h-[${icon.height}px]` : 'h-[72px]',
          ]"
        />
      </div>
      <div
        v-if="!icon?.fileName && fields.image?.value?.src"
        class="flex items-center justify-center h-[88px] w-[88px]"
      >
        <Image :media="fields.image" />
      </div>
      <div class="self-stretch flex-col justify-start items-center gap-4 flex">
        <div class="pb-2 flex-col justify-start items-center flex">
          <div class="justify-end items-end gap-1 inline-flex">
            <div class="text-center text-racq-dark-grey text-xl4-bold">
              {{ isMonthly ? monthlyPremium : yearlyAnnualPremium }}
            </div>
            <div class="justify-center items-center gap-2.5 flex">
              <div class="text-center text-racq-med-grey text-lg-bold">
                {{ isMonthly ? fields.monthly.value : fields.annually.value }}
              </div>
            </div>
          </div>

          <div v-if="isMonthly" class="justify-end items-end gap-1 inline-flex">
            <div class="text-center text-racq-med-grey text-xl">
              {{ monthlyAnnualPremium }}
            </div>
            <div class="pb-0.5 justify-start items-center gap-2.5 flex">
              <div class="text-racq-med-grey text-base">{{ fields.annually.value.replace('*', '') }}</div>
            </div>
          </div>
        </div>
        <div class="md:pb-4 flex-col justify-start items-center gap-6 flex">
          <FormKit type="toggle" v-bind="paymentFrequencyBindings" />
          <Tag v-if="onlineDiscount" :tagText="onlineDiscount" :tagType="TagTypes.discount" :tagSize="TagSize.sm" />
        </div>
      </div>
    </div>
  </div>
  <div v-show="loading">
    <Skeleton :blocks="skeletonBlocks" />
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { Text, Field, Image } from '@sitecore-jss/sitecore-jss-vue';
import { ImageField } from '@sitecore-jss/sitecore-jss-vue/types/components/Image';
import { formatCurrency } from '@/utils/text-transforms';
import Tag from '@/components/Tag/Tag.vue';
import { TagSize, TagTypes } from '@/enums/tag-enums';
import { useI18n } from 'vue-i18n';
import { QuoteTypeCodes, IQuote, PaymentFrequency, getInsuranceTypeDisplayName } from '@/interfaces';
import { getOnlineDiscount } from '@/utils/iq-discounts';
import Skeleton from '@/components/Skeleton/Skeleton.vue';
import { IImage } from '@/interfaces/forms/image-interface';

const translation = useI18n();
const dictionary: Record<string, string> = translation.getLocaleMessage('en') as any;
const skeletonBlocks = [
  {
    height: 26,
    width: 338,
    class: 'mb-3',
  },
  {
    height: 26,
    width: 176,
    class: 'mb-3',
  },
  {
    height: 88,
    width: 88,
    class: 'mb-3',
  },
  {
    height: 36,
    width: 244,
    class: 'mb-2',
  },
  {
    height: 36,
    width: 244,
    class: 'mb-6',
  },
  {
    height: 48,
    width: 204,
    class: 'mb-6',
  },
  {
    height: 26,
    width: 204,
    class: 'mb-5',
  },
];

const props = defineProps<{
  fields: {
    headingText: Field<any>;
    image: ImageField;
    monthly: Field<any>;
    annually: Field<any>;
  };
  quote: Partial<IQuote>;
  paymentFrequencyBindings: any;
  loading?: boolean;
  icon?: IImage;
}>();

const headingTextReplaced = computed(() =>
  props.fields.headingText.value.replace(
    '{{insuranceType}}',
    getInsuranceTypeDisplayName(dictionary, QuoteTypeCodes[props.quote.quoteType!])
  )
);

const onlineDiscount = computed(() =>
  getOnlineDiscount(translation, props.quote, {
    showAsMonthly: props.quote.paymentFrequency == PaymentFrequency.Monthly,
  })
);

const isMonthly = computed(() => props.quote.paymentFrequency == PaymentFrequency.Monthly);
const monthlyPremium = computed(() => formatCurrency(props.quote.insuranceQuoteSummary?.monthlyDeposit ?? 0, 2));
const monthlyAnnualPremium = computed(() =>
  formatCurrency(props.quote.insuranceQuoteSummary?.monthlyAnnualPremium ?? 0, 2)
);
const yearlyAnnualPremium = computed(() =>
  formatCurrency(props.quote.insuranceQuoteSummary?.yearlyAnnualPremium ?? 0, 2)
);
</script>
